import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useApiCatalog } from './queries';
import { hasOps } from '../../lib/auth';
import { useAuthStore } from '../../store/authStore';
import type { CatalogFilters } from './types';
import { defaultCatalogFilters } from './schemas';

// Components

function HealthPill({ healthy }: { healthy: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        healthy
          ? 'bg-green-100 text-green-800'
          : 'bg-red-100 text-red-800'
      }`}
    >
      {healthy ? '●' : '●'} {healthy ? 'Healthy' : 'Down'}
    </span>
  );
}

function AuthPill({ auth }: { auth: string }) {
  const colorMap: Record<string, string> = {
    jwt: 'bg-blue-100 text-blue-800',
    none: 'bg-gray-100 text-gray-800',
    key: 'bg-purple-100 text-purple-800',
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorMap[auth] || 'bg-gray-100 text-gray-800'}`}>
      {auth.toUpperCase()}
    </span>
  );
}

function PluginChips({ plugins }: { plugins: string[] }) {
  if (!plugins.length) return <span className="text-gray-400">None</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {plugins.slice(0, 3).map((plugin) => (
        <span
          key={plugin}
          className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs"
        >
          {plugin.replace(/^plg_.*?_/, '')}
        </span>
      ))}
      {plugins.length > 3 && (
        <span className="text-xs text-gray-500">+{plugins.length - 3}</span>
      )}
    </div>
  );
}

function TrafficMetrics({ metrics }: { metrics?: { rqps: number; p95: number } }) {
  if (!metrics) return <span className="text-gray-400">—</span>;

  return (
    <div className="text-sm">
      <div>{metrics.rqps.toFixed(1)} rq/s</div>
      <div className="text-gray-500">{(metrics.p95 * 1000).toFixed(0)}ms p95</div>
    </div>
  );
}

function FiltersBar({ 
  filters, 
  onFiltersChange 
}: { 
  filters: CatalogFilters; 
  onFiltersChange: (filters: CatalogFilters) => void; 
}) {
  const { t } = useTranslation();

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        {/* Search */}
        <div className="md:col-span-2">
          <input
            type="text"
            placeholder={t('apis.catalog.searchPlaceholder', 'Search services, routes...')}
            value={filters.search}
            onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Health Filter */}
        <div>
          <select
            value={filters.health}
            onChange={(e) => onFiltersChange({ ...filters, health: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">{t('apis.catalog.allHealth', 'All Health')}</option>
            <option value="healthy">{t('apis.catalog.healthyOnly', 'Healthy Only')}</option>
            <option value="unhealthy">{t('apis.catalog.unhealthyOnly', 'Unhealthy Only')}</option>
          </select>
        </div>

        {/* Auth Filter */}
        <div>
          <select
            value={filters.auth}
            onChange={(e) => onFiltersChange({ ...filters, auth: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">{t('apis.catalog.allAuth', 'All Auth')}</option>
            <option value="jwt">JWT</option>
            <option value="none">None</option>
            <option value="key">API Key</option>
          </select>
        </div>

        {/* Plugin Filter */}
        <div>
          <input
            type="text"
            placeholder={t('apis.catalog.pluginFilter', 'Plugin name...')}
            value={filters.plugin}
            onChange={(e) => onFiltersChange({ ...filters, plugin: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Clear Filters */}
        <div>
          <button
            onClick={() => onFiltersChange(defaultCatalogFilters)}
            className="w-full px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {t('apis.catalog.clearFilters', 'Clear')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ServiceTable({ 
  services, 
  routes, 
  plugins 
}: { 
  services: any[]; 
  routes: any[]; 
  plugins: any[]; 
}) {
  const { t } = useTranslation();

  if (!services.length) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-gray-400 text-lg mb-2">🛣️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('apis.catalog.noServices', 'No API services found')}
        </h3>
        <p className="text-gray-600 mb-4">
          {t('apis.catalog.noServicesDescription', 'Create your first API service to expose backend services through Kong.')}
        </p>
        <Link
          to="/apis/create"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {t('apis.catalog.createFirstApi', 'Create First API')}
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('apis.catalog.service', 'Service')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('apis.catalog.routes', 'Routes')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('apis.catalog.traffic', 'Traffic')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('apis.catalog.plugins', 'Plugins')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('apis.catalog.actions', 'Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {services.map((service) => {
              const serviceRoutes = routes.filter(r => r.serviceId === service.id);
              const servicePlugins = plugins.filter(p => p.serviceId === service.id);

              return (
                <tr key={service.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {service.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {service.host}:{service.port}
                        </div>
                      </div>
                      <div className="ml-3">
                        <HealthPill healthy={service.healthy} />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {serviceRoutes.slice(0, 2).map((route) => (
                        <div key={route.id} className="text-sm">
                          <span className="font-medium">{route.paths.join(', ')}</span>
                          <span className="ml-2 text-gray-500">{route.methods.join(', ')}</span>
                          <span className="ml-2">
                            <AuthPill auth={route.auth} />
                          </span>
                        </div>
                      ))}
                      {serviceRoutes.length > 2 && (
                        <div className="text-xs text-gray-500">
                          +{serviceRoutes.length - 2} more routes
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <TrafficMetrics metrics={service.metrics} />
                  </td>
                  <td className="px-6 py-4">
                    <PluginChips plugins={servicePlugins.map(p => p.name)} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex space-x-2">
                      <Link
                        to={`/apis/${service.id}`}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        {t('apis.catalog.viewService', 'View')}
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Catalog() {
  const { t } = useTranslation();
  const { selectedTenant } = useAuthStore();
  const canManage = hasOps();
  
  const [filters, setFilters] = useState<CatalogFilters>({
    ...defaultCatalogFilters,
    tenant: selectedTenant || 'dev',
  });

  const { data: catalog, isLoading, error } = useApiCatalog();

  // Filter data based on current filters
  const filteredData = useMemo(() => {
    if (!catalog) return { services: [], routes: [], plugins: [] };

    let services = catalog.services;
    let routes = catalog.routes;
    let plugins = catalog.plugins;

    // Apply search filter
    if (filters.search) {
      const search = filters.search.toLowerCase();
      services = services.filter(s => 
        s.name.toLowerCase().includes(search) ||
        s.host.toLowerCase().includes(search)
      );
      routes = routes.filter(r => 
        r.paths.some(p => p.toLowerCase().includes(search)) ||
        services.some(s => s.id === r.serviceId)
      );
    }

    // Apply health filter
    if (filters.health !== 'all') {
      const isHealthy = filters.health === 'healthy';
      services = services.filter(s => s.healthy === isHealthy);
    }

    // Apply auth filter
    if (filters.auth !== 'all') {
      const serviceIds = routes
        .filter(r => r.auth === filters.auth)
        .map(r => r.serviceId);
      services = services.filter(s => serviceIds.includes(s.id));
    }

    // Apply tenant filter
    if (filters.tenant) {
      services = services.filter(s => 
        s.tags.includes(`tenant:${filters.tenant}`)
      );
    }

    // Apply plugin filter
    if (filters.plugin) {
      const serviceIds = plugins
        .filter(p => p.name.toLowerCase().includes(filters.plugin.toLowerCase()))
        .map(p => p.serviceId)
        .filter(Boolean);
      services = services.filter(s => serviceIds.includes(s.id));
    }

    // Filter routes and plugins to match filtered services
    const filteredServiceIds = services.map(s => s.id);
    routes = routes.filter(r => filteredServiceIds.includes(r.serviceId));
    plugins = plugins.filter(p => !p.serviceId || filteredServiceIds.includes(p.serviceId));

    return { services, routes, plugins };
  }, [catalog, filters]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    const isAuthError = error.message.includes('401') || error.message.includes('Unauthorized') || error.message.includes('No authentication');
    
    return (
      <div className={`border rounded-lg p-6 ${isAuthError ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex">
          <div className={`text-xl mr-3 ${isAuthError ? 'text-yellow-600' : 'text-red-600'}`}>
            {isAuthError ? '🔐' : '⚠️'}
          </div>
          <div>
            <h3 className={`text-lg font-medium ${isAuthError ? 'text-yellow-800' : 'text-red-800'}`}>
              {isAuthError 
                ? t('apis.catalog.authRequired', 'Authentication Required')
                : t('apis.catalog.error', 'Error loading API catalog')
              }
            </h3>
            <p className={`mt-1 ${isAuthError ? 'text-yellow-700' : 'text-red-700'}`}>
              {isAuthError 
                ? t('apis.catalog.authRequiredMessage', 'Please log in with a valid JWT token to access the API Manager.')
                : error.message
              }
            </p>
            {isAuthError && (
              <p className="text-xs text-yellow-600 mt-2">
                💡 {t('apis.catalog.authHint', 'Check if your session has expired or contact your administrator for API access.')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {t('apis.catalog.title', 'API Catalog')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('apis.catalog.subtitle', 'Manage services, routes, and plugins')}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="text-sm">
            <span className="text-gray-500">{t('apis.catalog.tenant', 'Tenant')}:</span>
            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded font-medium">
              {selectedTenant || 'dev'}
            </span>
          </div>
          <Link
            to="/apis/test"
            className="px-4 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {t('apis.catalog.testConsole', 'Test Console')}
          </Link>
          {canManage && (
            <Link
              to="/apis/create"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {t('apis.catalog.createApi', 'Create API')}
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <FiltersBar filters={filters} onFiltersChange={setFilters} />

      {/* Results summary */}
      <div className="text-sm text-gray-600">
        {t('apis.catalog.resultsCount', 'Showing {{count}} services', { 
          count: filteredData.services.length 
        })}
        {filters.search && (
          <span className="ml-2">
            {t('apis.catalog.searchResults', 'for "{{search}}"', { search: filters.search })}
          </span>
        )}
      </div>

      {/* Services Table */}
      <ServiceTable 
        services={filteredData.services}
        routes={filteredData.routes}
        plugins={filteredData.plugins}
      />
    </div>
  );
}
